<template>
  <section class="cart-section d-flex align-items-center px-4">
    <div class="container pb-5">
      <div class="row g-4">
        <!-- Cart Items Section -->
        <div class="col-lg-7">
          <div class="cart-container p-4 rounded-3">
            <!-- Cart Header -->
            <div class="d-flex justify-content-between align-items-center border-bottom border-secondary pb-3 mb-4">
              <h2 class="cart-title text-white mb-0 py-3">
                Mi Carrito ({{getQuantity()}})
              </h2>
            </div>

            <!-- Empty Cart State -->
            <div v-if="cartItems.length === 0" class="text-center py-5">
              <i class="fas fa-shopping-cart fa-3x text-muted mb-4"></i>
              <h3 class="text-white mb-3">Tu carrito está vacío</h3>
              <p class="text-muted mb-4">¡Agrega productos para comenzar tu compra!</p>
              <a class="btn btn-primary btn-lg" href="/productos">
                <i class="fas fa-store me-2"></i>
                Explorar Productos
              </a>
            </div>

            <!-- Cart Items -->
            <div v-else class="cart-items">
              <TransitionGroup name="list" tag="div" class="d-flex flex-column gap-3">
                <div 
                  v-for="item in cartItems" 
                  :key="item.id"
                  class="cart-item p-3 rounded-3 shadow-sm"
                >
                  <div class="row py-2">
                    <!-- Product Image -->
                    <div class="col-3">
                      <div class="img-wrapper rounded-3 overflow-hidden p-2">
                        <img 
                          :src="item.image || require('@/assets/zapiola-logo.png')" 
                          :alt="item.name"
                          class="img-fluid hover-zoom"
                        />
                      </div>
                    </div>
                    
                    <!-- Product Details - Ajustado para móvil -->
                    <div class="col-9 ps-0">
                      <div class="d-flex flex-column h-100">
                        <div class="d-flex justify-content-between mb-2">
                          <div>
                            <h4 class="text-white mb-1 fs-6">{{ item.name }}</h4>
                            <span class="text-muted fs-7">{{ item.brand }}</span>
                          </div>
                          <button 
                            class="btn btn-outline-danger btn-sm" 
                            @click="removeItem(item.id)"
                            title="Eliminar producto"
                          >
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </div>
                        
                        <!-- Price and Quantity Controls -->
                        <div class="mt-auto">
                          <div class="d-flex justify-content-between align-items-center">
                            <div class="quantity-wrapper">
                              <input 
                                type="number" 
                                class="form-control quantity-input"
                                :value="item.quantity"
                                min="1"
                                max="99"
                                @change="e => updateQuantity(item.id, parseInt(e.target.value))"
                              />
                            </div>
                            <div class="text-end">
                              <div class="price-tag fs-7 fw-bold">
                                {{ formatPrice(item.price * item.quantity) }}
                              </div>
                              <small class="text-muted fs-7">
                                {{ formatPrice(item.price) }} c/u
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
            </div>
          </div>
        </div>

        <!-- Order Summary Section -->
        <div class="col-lg-5">
          <div v-if="cartItems.length > 0" class="summary-container p-4 rounded-3 sticky-top" style="top: 2rem;">
            <h3 class="text-white mb-4">Resumen de Orden</h3>
            
            <!-- Shipping Information Form -->
            <form v-if="showCheckoutForm" @submit.prevent="submitOrder" class="mb-4">
              <!-- Recipient Information -->
              <div class="form-section mb-4">
                <h6 class="form-section-title mb-3 text-white">
                  <i class="fas fa-user me-2"></i>Datos del destinatario
                </h6>
                <div class="form-group mb-3">
                  <label class="form-label text-white fw-light">Nombre completo</label>
                  <input 
                    v-model="orderData.recipient_name"
                    type="text"
                    class="form-control"
                    placeholder="Nombre de quien recibe el pedido"
                    required
                  />
                  <div class="invalid-feedback" v-if="v$.recipient_name.$error">
                    {{ v$.recipient_name.$errors[0].$message }}
                  </div>
                </div>
                <div class="form-group mb-3">
                  <label class="form-label text-white fw-light">Email de contacto *</label>
                  <input 
                    v-model="orderData.customer_email"
                    type="email"
                    class="form-control"
                    placeholder="Ej: ejemplo@gmail.com"
                    required
                  />
                  <div class="invalid-feedback" v-if="v$.customer_email.$error">
                    {{ v$.customer_email.$errors[0].$message }}
                  </div>  
                </div>
            
                <div class="form-group mb-3">
                  <label class="form-label text-white fw-light">Teléfono de contacto</label>
                  <input 
                    v-model="orderData.customer_phone"
                    type="tel"
                    class="form-control"
                    placeholder="Ej: 1123456789"
                    required
                  />
                  <div class="invalid-feedback" v-if="v$.customer_phone.$error">
                    {{ v$.customer_phone.$errors[0].$message }}
                  </div>
                </div>
              </div>
            
              <!-- Delivery Options -->
              <div class="form-section mb-4">
                <h6 class="form-section-title mb-3">
                  <i class="fas fa-truck me-2"></i>Método de entrega
                </h6>
                <div class="delivery-options">
                  <div class="form-check custom-radio mb-3">
                    <input
                      type="radio"
                      id="deliveryOption1"
                      v-model="orderData.delivery_type"
                      value="address"
                      class="form-check-input"
                    >
                    <label class="form-check-label" for="deliveryOption1">
                      Envío a domicilio
                    </label>
                  </div>
                  <div class="form-check custom-radio mb-3">
                    <input
                      type="radio"
                      id="deliveryOption2"
                      v-model="orderData.delivery_type"
                      value="coordinate"
                      class="form-check-input"
                    >
                    <label class="form-check-label" for="deliveryOption2">
                      Acordar entrega
                    </label>
                  </div>
                </div>
            
                <!-- Address Input (shown only for delivery_type = 'address') -->
                <div v-if="orderData.delivery_type === 'address'" class="mt-3">
                  <div class="address-fields">
                    <div class="row g-3">
                      <div class="col-md-8">
                        <div class="form-group">
                          <label class="form-label text-white">Calle</label>
                          <input 
                            v-model="addressFields.street"
                            type="text"
                            class="form-control"
                            placeholder="Nombre de la calle"
                            required
                          />
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="form-group">
                          <label class="form-label text-white">Número</label>
                          <input 
                            v-model="addressFields.number"
                            type="text"
                            class="form-control"
                            placeholder="123"
                            required
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label class="form-label text-white">Código Postal</label>
                          <input 
                            v-model="addressFields.zipCode"
                            type="text"
                            class="form-control"
                            placeholder="1234"
                            required
                          />
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label class="form-label text-white">Ciudad</label>
                          <input 
                            v-model="addressFields.city"
                            type="text"
                            class="form-control"
                            placeholder="Ciudad"
                            required
                          />
                        </div>
                      </div>
                      <div class="col-12">
                        <div class="form-group">
                          <label class="form-label text-white">Detalles adicionales</label>
                          <textarea 
                            v-model="addressFields.details"
                            class="form-control"
                            rows="2"
                            placeholder="Piso, departamento, entre calles, etc. (opcional)"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            
                <!-- Coordination Message (shown only for delivery_type = 'coordinate') -->
                <div v-if="orderData.delivery_type === 'coordinate'" class="mt-3">
                  <div class="coordinate-info alert alert-info">
                    <i class="fas fa-info-circle me-2"></i>
                    Te contactaremos por email para coordinar la entrega
                  </div>
                </div>
              </div>
            </form>
        
            <!-- Summary Details -->
            <div class="summary-details">
              <div class="d-flex justify-content-between mb-3">
                <span class="text-muted">Subtotal</span>
                <span class="text-white">{{ formatPrice(cartSubtotal) }}</span>
              </div>
              <div class="border-top border-secondary pt-3 mt-3">
                <div class="d-flex justify-content-between">
                  <span class="text-white fw-bold">Total</span>
                  <span class="text-white fw-bold h4 mb-0">
                    {{ formatPrice(cartTotal) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Remove Free Shipping Alert -->

            <!-- Action Buttons -->
            <div class="d-grid gap-3 mt-4">
              <div class="alert alert-info mb-3" v-if="showCheckoutForm">
                <i class="fas fa-info-circle me-2"></i>
                <small>
                  Una vez confirmado el pedido, recibirás un email con la confirmación y los pasos a seguir para el pago y la entrega.
                </small>
              </div>
              <button 
                v-if="!showCheckoutForm"
                class="btn btn-primary btn-lg py-3 px-4 fs-6" 
                @click="startCheckout"
              >
                <i class="fas fa-shopping-cart me-2"></i>
                Iniciar Pedido
              </button>
        
              <button 
                v-else
                type="submit"
                class="btn btn-primary btn-lg py-3 px-4 fs-6" 
                @click="submitOrder"
                :disabled="isLoading || !isFormValid"
              >
                <span v-if="isLoading">
                  <i class="fas fa-spinner fa-spin me-2"></i>
                  Procesando...
                </span>
                <span v-else>
                  <i class="fas fa-check me-2"></i>
                  Confirmar Pedido
                </span>
              </button>
        
              <button 
                class="btn btn-outline-light py-3 px-4 fs-6" 
                @click="showCheckoutForm ? cancelCheckout() : closeCart()"
              >
                <i class="fas fa-arrow-left me-2"></i>
                {{ showCheckoutForm ? 'Volver' : 'Seguir Comprando' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { createOrder } from '@/services/orders';
import { useToast } from 'vue-toastification';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';

const store = useStore();
const router = useRouter();
const toast = useToast();
const isLoading = ref(false);

const cartItems = computed(() => store.getters.cartItems);
const cartSubtotal = computed(() => {
  return cartItems.value.reduce((total, item) => total + (item.price * item.quantity), 0);
});

// Remove shipping computed property
const cartTotal = computed(() => cartSubtotal.value);


onMounted(() => {
  // seteo el titulo de la página
  document.title = 'Carrito de Compras - Zapiola Growshop';
});

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'ARS',
  }).format(price);
};

const getQuantity = () => {
  return cartItems.value.reduce((total, item) => total + item.quantity, 0);
};


const updateQuantity = async (productId, quantity) => {
  try {
    const item = cartItems.value.find(item => item.id === productId);
    
    // Validar contra el stock del producto directamente
    if (quantity > item.stock) {
      toast.error(`Solo hay ${item.stock} unidades disponibles`);
      return;
    }

    const validQuantity = Math.max(1, Math.min(item.stock, quantity));
    store.dispatch('updateQuantity', { productId, quantity: validQuantity });
  } catch (error) {
    console.error('Error updating quantity:', error);
    toast.error('Error al actualizar la cantidad');
  }
};

const removeItem = (productId) => {
  store.dispatch('removeFromCart', productId);
};

const closeCart = () => {
  store.dispatch('setCartOpen', false);
};

const showCheckoutForm = ref(false);
const orderData = reactive({
  recipient_name: '',
  shipping_address: '',
  customer_email: '',
  customer_phone: '',
  delivery_type: 'coordinate'
});

const addressFields = reactive({
  street: '',
  number: '',
  zipCode: '',
  city: '',
  details: ''
});

// Modificación de las reglas de validación
const rules = {
  recipient_name: { 
    required: helpers.withMessage('El nombre es requerido', required),
    minLength: helpers.withMessage('El nombre debe tener al menos 3 caracteres', minLength(3))
  },
  customer_phone: {
    required: helpers.withMessage('El teléfono es requerido', required),
    validPhone: helpers.withMessage('Ingresa un teléfono válido', helpers.regex(/^[0-9]{10}$/))
  },
  delivery_type: {
    required: helpers.withMessage('Selecciona un método de entrega', required)
  },
  customer_email: {
    required: helpers.withMessage('El email es requerido', required),
    email: helpers.withMessage('Ingresa un email válido', helpers.regex(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
  }
};

// Watch para limpiar la dirección cuando se cambia el tipo de entrega
watch(() => orderData.delivery_type, (newType) => {
  if (newType === 'coordinate') {
    orderData.shipping_address = '';
    // Revalidar el formulario
    v$.value.$touch();
  }
});

// Watch para actualizar shipping_address y validar
watch([addressFields, () => orderData.delivery_type], () => {
  if (orderData.delivery_type === 'address') {
    const { street, number, zipCode, city, details } = addressFields;
    const requiredFields = [street, number, zipCode, city];
    
    // Verificar si los campos requeridos están completos
    const isAddressValid = requiredFields.every(field => field && field.trim() !== '');
    
    if (isAddressValid) {
      orderData.shipping_address = [
        `${street} ${number}`,
        zipCode,
        city,
        details
      ].filter(Boolean).join(', ');
    } else {
      orderData.shipping_address = '';
    }
  } else {
    orderData.shipping_address = 'Coordinar entrega';
  }
}, { deep: true });

const isFormValid = computed(() => {
  if (!orderData.recipient_name || !orderData.customer_phone || !orderData.delivery_type) {
    return false;
  }

  if (orderData.delivery_type === 'address') {
    const { street, number, zipCode, city } = addressFields;
    return !!(street && number && zipCode && city);
  }

  return true;
});

const v$ = useVuelidate(rules, orderData);

const startCheckout = () => {
  if (!localStorage.getItem('accessToken')) {
    toast.info('Debes iniciar sesión para realizar la compra');
    router.push('/iniciar-sesion?redirect=carrito');
    return;
  }
  showCheckoutForm.value = true;
};

const cancelCheckout = () => {
  showCheckoutForm.value = false;
  orderData.customer_phone = '';
  // Limpiar campos de dirección
  Object.keys(addressFields).forEach(key => {
    addressFields[key] = '';
  });
};

const submitOrder = async () => {
  try {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid) return;

    isLoading.value = true;

    // Verificar stock de productos
    for (const item of cartItems.value) {
      if (item.quantity > item.stock) {
        toast.error(`No hay suficiente stock de ${item.name}. Stock disponible: ${item.stock}`);
        return;
      }
    }

    const orderPayload = {
      recipient_name: orderData.recipient_name,
      shipping_address: orderData.delivery_type === 'address' ? orderData.shipping_address : 'Coordinar entrega',
      customer_phone: orderData.customer_phone,
      customer_email: orderData.customer_email,
      delivery_type: orderData.delivery_type,
      products: cartItems.value.map(item => ({
        product_id: item.id,
        quantity: item.quantity
      }))
    };

    await createOrder(orderPayload);
    
    store.dispatch('clearCart');
    toast.success('¡Orden creada exitosamente! Te enviaremos un email con los detalles y próximos pasos.');
    router.push('/mi-cuenta?section=orders');
  } catch (error) {
    console.error('Error creating order:', error);
    toast.error(error.response?.data?.message || 'Error al procesar la orden');
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.cart-section {
  min-height: 100vh;
  background-color: var(--bg-color);
}

.cart-container,
.summary-container {
  border: 1px solid rgba(255, 255, 255, 0.1);
  background-color: var(--black-color-3);
}

.cart-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.cart-item {
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.form-control{
  background-color: var(--black-color-3);
  color: var(--white-color);
}

.img-wrapper {
  aspect-ratio: 1;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.05);
}

.img-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.img-wrapper:hover img {
  transform: scale(1.05);
}

.price-tag {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--white-color);
}

.quantity-controls input[type="number"] {
  max-width: 60px;
}

.quantity-controls input[type="number"]::-webkit-inner-spin-button,
.quantity-controls input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
}

/* Transition animations for cart items */
.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.list-leave-active {
  position: absolute;
}

/* Sticky summary fixes */
@media (min-width: 992px) {
  .summary-container {
    position: sticky;
    top: 2rem;
  }
}

/* Responsive adjustments */
@media (max-width: 991.98px) {
  .cart-section {
    padding-top: 2rem;
  }
  
  .summary-container {
    margin-top: 2rem;
  }
}

@media (max-width: 767.98px) {
  .cart-item {
    padding: 1rem;
  }
  
  .quantity-controls {
    flex-direction: column;
    align-items: flex-start;
  }
}

.form-control {
  background-color: var(--black-color-2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--white-color);
}

.form-control:focus {
  background-color: var(--black-color-2);
  border-color: var(--primary-color);
  color: var(--white-color);
  box-shadow: none;
}

.form-label {
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-section {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.form-section-title {
  color: var(--white-color);
  font-weight: 500;
  margin-bottom: 1rem;
}

.custom-radio .form-check-input {
  background-color: var(--black-color-2);
  border-color: rgba(255, 255, 255, 0.2);
}

.custom-radio .form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.custom-radio .form-check-label {
  color: var(--white-color);
  font-size: 0.95rem;
}

.coordinate-info {
  background: rgba(13, 202, 240, 0.1);
  border-left: 4px solid var(--bs-info);
  color: var(--white-color);
}

.address-fields {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  padding: 1rem;
}

/* Mobile Optimizations */
@media (max-width: 767px) {
  .cart-section {
    padding: 1rem;
  }

  .cart-container,
  .summary-container {
    padding: 1rem !important;
  }

  .cart-item {
    padding: 0.75rem !important;
  }

  .img-wrapper {
    aspect-ratio: 1;
    width: 100%;
    border-radius: 8px;
  }

  .form-section {
    padding: 1rem;
  }

  .quantity-controls {
    flex-direction: row !important;
    align-items: center !important;
  }

  .input-group {
    width: auto !important;
  }

  .input-group input {
    text-align: center;
    padding: 0.25rem !important;
  }

  .btn-outline-secondary {
    padding: 0.25rem 0.5rem !important;
  }

  /* Ajustar el espacio del resumen en móvil */
  .summary-container {
    margin-top: 1rem;
    position: static;
  }

  /* Mejorar legibilidad de precios */
  .price-tag {
    font-size: 1rem !important;
  }

  /* Ajustar campos del formulario */
  .form-control {
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
  }

  /* Ajustar botones de acción */
  .btn-lg {
    padding: 0.75rem 1rem !important;
    font-size: 0.9rem !important;
  }
}

.quantity-wrapper {
  width: 60px;
}

.quantity-input {
  background-color: var(--black-color-2) !important;
  color: var(--white-color) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  height: 36px;
  padding: 0.25rem;
  text-align: center;
  font-weight: 500;
}

.quantity-input:focus {
  border-color: var(--primary-color);
  box-shadow: none;
  outline: none;
}

.quantity-input::-webkit-inner-spin-button {
  opacity: 1;
  height: 36px;
}

@media (max-width: 767px) {
  .quantity-wrapper {
    width: 50px;
  }
  
  .quantity-input {
    height: 32px;
    font-size: 0.9rem;
  }
}
</style>